import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import {
  operations as siteOperations,
  actions as siteActions,
} from 'store/siteManager'
import { operations as lmsOperations } from 'store/lmsManager'
import { operations as licenseOperations } from 'store/licenseManager'
import { operations as userContextOperations } from 'auth/stores/userContext'
import SecondStepApi from 'lib/api/secondStepApi'
import { Formik } from 'formik'
import { validationSchema, fieldNames, generateContext } from './data'
import ProgramRoleModalComponent from './component'
import { useUserContext } from 'layers/content/v2/hooks/useUserContext'

export const ProgramRoleModalContainer = ({
  clearError,
  fetchOrCreateInstructorProgramFlex,
  fetchUserContext,
  fetchUserSites,
  hasApiError,
  handleClose,
  instructorData,
  entry,
  updateSiteUserPreferences,
  programPreferences,
}) => {
  const { yearsTeaching, teachesLessons } = programPreferences || {}
  const userContext = useUserContext()

  const { choiceContent = [] } = entry || {}
  const { answerChoice1: teachChoice1, answerChoice2: teachChoice2 } =
    choiceContent[0] || {}
  const teachOptions = {
    [teachChoice1]: true,
    [teachChoice2]: false,
  }
  const { answerChoice1: yearsChoice1 } = choiceContent[1] || {}
  const handleSave = async values => {
    const { sites = [] } = userContext || {}
    const site = sites[0] || {}
    const { claims = [] } = site || {}

    const isAdmin = claims.some(claim => claim === 'admin')
    const teachesLessons = teachOptions?.[values.teachesLessonsAnswer]
    const programTrack = teachesLessons || isAdmin ? 'Instructor' : 'Staff'

    const newSitePreferences = {
      k5Digital: {
        userProgramPreferences: {
          teachesLessons,
          yearsTeaching: values.yearsTeaching,
          programTrack,
        },
      },
      mspDigital: {
        userProgramPreferences: {
          teachesLessons,
          yearsTeaching: values.yearsTeaching,
          programTrack,
        },
      },
    }

    await updateSiteUserPreferences(site.siteId, newSitePreferences)
    if (
      !instructorData?.K5SelDigital?.educator?.length > 0 ||
      !instructorData?.MspDigital?.educator?.length > 0
    ) {
      // TODO: update the generateContext helper to pull course from context rather than hardcoding it
      const k5Context = generateContext(
        'k-5_training_tier1',
        site.siteId,
        claims,
        programTrack,
        'k5SelDigital',
      )
      const mspContext = generateContext(
        '6-8_training_tier1',
        site.siteId,
        claims,
        programTrack,
        'mspDigital',
      )

      await fetchOrCreateInstructorProgramFlex(site.siteId, k5Context)
      await fetchOrCreateInstructorProgramFlex(site.siteId, mspContext)
    }

    // TODO: refactor updateSiteUserPreferences to update user context with values so sites don't need to be refetched
    await fetchUserSites()
    await fetchUserContext(SecondStepApi.baseApiAddress)
  }

  const closeModal = () => {
    clearError('sitePreferences')
    handleClose()
  }

  const hasTeachesLessons =
    teachesLessons !== undefined && teachesLessons !== null
  const initialValues = {
    teachesLessonsAnswer: hasTeachesLessons
      ? teachesLessons
        ? teachChoice1
        : teachChoice2
      : teachChoice1,
    yearsTeaching: yearsTeaching || yearsChoice1,
  }

  return entry ? (
    <Formik
      initialTouched={{
        teachesLessonsAnswer: !!teachesLessons,
        yearsTeaching: !!yearsTeaching,
      }}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        setSubmitting,
        setTouched,
        isSubmitting,
        isValid,
      }) => (
        <ProgramRoleModalComponent
          entry={entry}
          errors={errors}
          handleChange={(name, value) => {
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleClose={closeModal}
          handleNextButtonClick={async () => {
            const isValidSync = validationSchema.isValidSync(values)
            if (!isValidSync) {
              const touchedFields = fieldNames.reduce((acc, name) => {
                acc[name] = true
                return acc
              }, {})
              await setTouched(touchedFields)
              return
            }
            setSubmitting(true)
            await handleSave(values)
            setSubmitting(false)
            closeModal()
          }}
          hasApiError={hasApiError}
          isSubmitting={isSubmitting}
          isValid={isValid}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  ) : null
}

const mapStateToProps = state => {
  const { sitePreferences: sitePreferencesAPIResults } = state.siteManager
  const { error } = sitePreferencesAPIResults

  return {
    hasApiError: !!error,
  }
}

const mapDispatchToProps = {
  updateSiteUserPreferences: siteOperations.updateSiteUserPreferences,
  fetchUserSites: licenseOperations.fetchUserSites,
  fetchOrCreateInstructorProgramFlex:
    lmsOperations.fetchOrCreateInstructorProgramFlex,
  fetchUserContext: userContextOperations.fetchUserContextReduxConnect,
  clearError: siteActions.clearError,
}

ProgramRoleModalContainer.propTypes = {
  clearError: PropTypes.func,
  entry: PropTypes.object,
  fetchOrCreateInstructorProgramFlex: PropTypes.func,
  fetchUserContext: PropTypes.func,
  fetchUserSites: PropTypes.func,
  handleClose: PropTypes.func,
  hasApiError: PropTypes.bool,
  instructorData: PropTypes.object,
  programPreferences: PropTypes.object,
  siteId: PropTypes.number,
  teachesLessons: PropTypes.bool,
  updateSiteUserPreferences: PropTypes.func,
  yearsTeaching: PropTypes.string,
}

const mapper = entry => {
  return {
    entry,
  }
}

const options = {
  entryId: '3fxndewvV5bkqTkLuTcMW4',
  include: 3,
  mapper,
  spread: true,
}

// TODO: use contentful data from K8ProgramStatusRouter instead of this
export default ContentEntryDataGetter(
  withApiError(
    ProgramRoleModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
